import { FIGURE } from '../diagramTheme';

// ```venn - two or three overlapping sets, as JSON:
//
//   { "sets": ["Mitosis", "Meiosis"],
//     "only": [["2 identical cells"], ["4 unique cells"]],
//     "both": ["DNA copied first"] }
//
// Three sets add `pairs` ({"0-1": [...], "0-2": [...], "1-2": [...]}) and use
// `both` for the centre (all three). Plain SVG in the figure theme; a spec
// that does not parse falls back to its source like every other fence.

export interface VennSpec {
  sets: string[];
  only?: string[][];
  both?: string[];
  pairs?: Record<string, string[]>;
}

const LINE = 14;

function Items({
  x,
  y,
  items,
  anchor = 'middle',
}: {
  x: number;
  y: number;
  items: string[];
  anchor?: 'start' | 'middle' | 'end';
}) {
  const top = y - ((items.length - 1) * LINE) / 2;
  return (
    <g fontFamily={FIGURE.font} fontSize={11} fill={FIGURE.ink}>
      {items.map((item, i) => (
        <text key={i} x={x} y={top + i * LINE} textAnchor={anchor}>
          {item}
        </text>
      ))}
    </g>
  );
}

function Circle({
  cx,
  cy,
  r,
  i,
}: {
  cx: number;
  cy: number;
  r: number;
  i: number;
}) {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={FIGURE.series[2 + (i % 3)]}
      fillOpacity={0.28}
      stroke={FIGURE.series[i % 2]}
      strokeWidth={1.5}
    />
  );
}

function SetName({ x, y, name }: { x: number; y: number; name: string }) {
  return (
    <text
      x={x}
      y={y}
      textAnchor='middle'
      fontFamily={FIGURE.font}
      fontSize={13}
      fontWeight={600}
      fill={FIGURE.brandDeep}
    >
      {name}
    </text>
  );
}

function Two({ spec }: { spec: VennSpec }) {
  const [a, b] = spec.sets;
  return (
    <svg viewBox='0 0 520 290' width='520' height='290'>
      <Circle cx={200} cy={155} r={125} i={0} />
      <Circle cx={320} cy={155} r={125} i={1} />
      <SetName x={160} y={20} name={a} />
      <SetName x={360} y={20} name={b} />
      <Items x={138} y={155} items={spec.only?.[0] ?? []} />
      <Items x={382} y={155} items={spec.only?.[1] ?? []} />
      <Items x={260} y={155} items={spec.both ?? []} />
    </svg>
  );
}

function Three({ spec }: { spec: VennSpec }) {
  const [a, b, c] = spec.sets;
  const pair = (k: string) => spec.pairs?.[k] ?? [];
  return (
    <svg viewBox='0 0 420 320' width='420' height='320'>
      <Circle cx={170} cy={125} r={88} i={0} />
      <Circle cx={250} cy={125} r={88} i={1} />
      <Circle cx={210} cy={195} r={88} i={2} />
      <SetName x={120} y={22} name={a} />
      <SetName x={300} y={22} name={b} />
      <SetName x={210} y={308} name={c} />
      <Items x={135} y={100} items={spec.only?.[0] ?? []} />
      <Items x={285} y={100} items={spec.only?.[1] ?? []} />
      <Items x={210} y={240} items={spec.only?.[2] ?? []} />
      <Items x={210} y={88} items={pair('0-1')} />
      <Items x={160} y={185} items={pair('0-2')} />
      <Items x={260} y={185} items={pair('1-2')} />
      <Items x={210} y={150} items={spec.both ?? []} />
    </svg>
  );
}

export function vennLabel(spec: VennSpec): string {
  return `Venn diagram comparing ${spec.sets.join(', ')}`;
}

export function VennFigure({ spec }: { spec: VennSpec }) {
  if (spec.sets.length === 3) return <Three spec={spec} />;
  return <Two spec={spec} />;
}
